/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
    InputField,
    InputPassword
} from '@zoopla-eng/zds-components/input-field';
import { Link } from '@zoopla-eng/zds-components/link';
import { Checkbox } from '@zoopla-eng/zds-components/checkbox';
import { Box } from '@zoopla-eng/zds-components/box';
import { Button } from '@zoopla-eng/zds-components/button';

import { EMAIL_REGEX, PASSWORD_MIN_LENGTH } from './rules';
import { useSignIn } from '../../hooks/useSignIn';
import { AuthMutationHookConsumerProps } from '../../hooks/authMutationHook.types';
import { triggerTracking } from '@zoopla/tracking';
import { SocialAuth } from '../SocialAuth/SocialAuth';
import {
    buttonContainerStyle,
    styledGeneralErrorStyle
} from '../shared/shared.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { ErrorAlert } from '../ErrorAlert';

interface SignInFormData {
    email: string;
    'current-password': string;
    rememberMe?: boolean;
}

const detailsEmailFieldLabelTn = 'Email address';
const detailsEmailFieldValidationTn = 'Please enter a valid email address';
const detailsPasswordFieldLabelTn = 'Password';
const forgotPasswordTn = 'Forgot password';
const detailsPasswordFieldGuidanceTn = 'Minimum 8 characters';
const signinButtonTn = 'Sign in';
const socialSignInTextTn = 'or sign in with your email';
const googleSignInTextTn = 'Google';
const facebookSignInTextTn = 'Facebook';

const socialCopy = {
    cta: socialSignInTextTn,
    facebook: facebookSignInTextTn,
    google: googleSignInTextTn
};

export const SignInForm = ({
    onAuthentication,
    trackingData
}: AuthMutationHookConsumerProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        getValues,
        control
    } = useForm<SignInFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur'
    });

    const getIsRememberMeChecked = () => getValues('rememberMe');

    const signInUser = useSignIn({
        trackingData
    });

    const onSubmit = handleSubmit(async (formData) => {
        setIsLoading(true);

        try {
            const user = await signInUser({
                email: formData.email,
                password: formData['current-password'],
                rememberMe: !!formData.rememberMe
            });

            await onAuthentication(user);
        } catch (error) {
            if (error instanceof Error) {
                if (error.message === 'Weak password, please try again.') {
                    return await onAuthentication(
                        { actions: [] },
                        {
                            email: formData.email,
                            password: formData['current-password'],
                            rememberMe: !!formData.rememberMe
                        }
                    );
                }
                setError(error.message);
            } else {
                setError('Sorry, something went wrong. Please try again.');
            }

            // we only need to update the loading state if we have an error
            // in case everything works as intended the user will be moved to the next step
            setIsLoading(false);
        }
    });

    const resetFormErrors = () => {
        // reset signin form general and field-specific errors
        setError('');
        const currentValues = getValues();

        reset({
            ...currentValues
        });
    };

    useEffect(() => {
        reset();
    }, []);

    return (
        <>
            <SocialAuth
                onAuthentication={onAuthentication}
                trackingData={trackingData}
                copy={socialCopy}
                type="signin"
                shouldReset={isLoading}
                resetErrors={resetFormErrors}
                isRememberMeChecked={getIsRememberMeChecked()}
            />
            <form
                onSubmit={onSubmit}
                noValidate
                data-testid="auth-modal-signin-form"
                method="post"
            >
                <ErrorAlert>
                    {error && (
                        <Box pb="S500">
                            <span
                                className={`${styledGeneralErrorStyle} ${fontGroups.fork}`}
                                data-testid="general-error"
                            >
                                {error}
                            </span>
                        </Box>
                    )}
                </ErrorAlert>
                <Box pb="S500">
                    <InputField
                        label={detailsEmailFieldLabelTn}
                        aria-required
                        type="email"
                        errorText={
                            errors.email &&
                            (errors.email.message ||
                                detailsEmailFieldValidationTn)
                        }
                        {...register('email', {
                            required: true,
                            pattern: EMAIL_REGEX
                        })}
                        data-testid="email-field-input"
                    />
                </Box>
                <Box pb="S500">
                    <InputPassword.Simple
                        label={detailsPasswordFieldLabelTn}
                        aria-required
                        errorText={
                            errors['current-password'] &&
                            (errors['current-password'].message ||
                                detailsPasswordFieldGuidanceTn)
                        }
                        {...register('current-password', {
                            required: true,
                            minLength: PASSWORD_MIN_LENGTH
                        })}
                        data-testid="password-field"
                    />
                </Box>
                <Box pb="M100" bpL100={{ pb: 'M300' }}>
                    <Link
                        href="/signin/forgot/"
                        onClick={() =>
                            triggerTracking({
                                event: 'GAEvent',
                                eventCategory: 'signin',
                                eventAction: 'click_forgotten_password',
                                eventLabel:
                                    trackingData?.label || 'signin_modal'
                            })
                        }
                    >
                        {forgotPasswordTn}
                    </Link>
                </Box>
                <Box pb="M100">
                    <Controller
                        control={control}
                        name="rememberMe"
                        render={({ field: { onChange, onBlur, name } }) => (
                            <Checkbox
                                label="Keep me signed in for 7 days"
                                data-testid="keep-me-signed-in-field"
                                aria-describedby="keep-me-sign-in"
                                checked={getValues('rememberMe')}
                                onCheckedChange={onChange}
                                onBlur={onBlur}
                                name={name}
                                helpText="If you're using a public device, please skip."
                            />
                        )}
                    />
                </Box>
                <Box display="flex" justifyContent="center">
                    <div className={buttonContainerStyle}>
                        <Button
                            category="primary"
                            loading={isLoading}
                            loadingText="Signing in"
                            data-testid="signin-button"
                            fw
                        >
                            {signinButtonTn}
                        </Button>
                    </div>
                </Box>
            </form>
        </>
    );
};
